// @ts-nocheck
import React, { useState, useEffect, useRef } from 'react';
import { Message } from '../../types/chat';
import { renderMarkdownContent } from '../../utils/markdownRenderer';
import FeedbackButtons from './FeedbackButtons';
import SaveAnswerButton from './SaveAnswerButton';
import BotIcon from './BotIcon';
import '../../styles/messageResponse.css';

interface StandardMessageDisplayProps {
  message: Message;
  userQuery?: string;
  conversationId?: string;
  clientId?: string;
  onFeedback?: (messageId: string, type: 'thumbs_up' | 'thumbs_down' | 'need_review', reason?: string) => void;
  theme?: any; // Theme configuration
  isWidgetMode?: boolean; // Whether this is in widget mode (public website)
  isAuthenticated?: boolean; // Whether user is authenticated
}

/**
 * StandardMessageDisplay - Citation display for Standard sidebar
 *
 * Key differences from modal variant (MessageResponseDisplay):
 * - NO tabs (Response/Sources) - single unified view
 * - Inline citation links (Grok-style) embedded in text
 * - Citations show as colored, clickable links with external icon
 * - Cleaner, more streamlined UI
 * - Theme-customizable citation colors
 */
const StandardMessageDisplay: React.FC<StandardMessageDisplayProps> = ({
  message,
  userQuery,
  conversationId,
  clientId,
  onFeedback,
  theme = {},
  isWidgetMode = false,
  isAuthenticated = true
}) => {
  const contentRef = useRef<HTMLDivElement>(null);
  const [feedbackState, setFeedbackState] = useState<string | null>(message.feedback || null);
  const [showReasonOptions, setShowReasonOptions] = useState(false);

  // Reason options for "need_review"
  const REASON_OPTIONS = [
    { value: 'incomplete_answer', label: 'Incomplete answer' },
    { value: 'wrong_answer', label: 'Wrong answer' },
    { value: 'wrong_citation', label: 'Wrong citation/source' },
    { value: 'no_response', label: 'No response' },
    { value: 'response_broken', label: 'Response broken/cut off' },
    { value: 'irrelevant_response', label: 'Irrelevant response' },
    { value: 'hallucination', label: 'Hallucination/made up info' },
    { value: 'poor_context', label: 'Poor context understanding' },
    { value: 'technical_error', label: 'Technical error' }
  ];

  // Citation styling from theme (moved to component level for <style> tag)
  const citationColor = theme.standardCitationLinkColor || '#2563EB';
  const citationBg = theme.standardCitationLinkBg || 'rgba(37, 99, 235, 0.08)';
  const citationBorder = theme.standardCitationLinkBorder || 'rgba(37, 99, 235, 0.2)';
  const citationHoverBg = theme.standardCitationLinkHoverBg || 'rgba(37, 99, 235, 0.15)';

  // Feedback styling from theme
  const feedbackIconColor = theme.chatFeedbackIconColor || '#9ca3af';
  const feedbackIconHoverColor = theme.chatFeedbackIconHoverColor || '#6b7280';
  const feedbackActiveColor = theme.chatFeedbackActiveColor || '#4080F2';
  const feedbackBg = theme.chatFeedbackBg || 'transparent';
  const feedbackHoverBg = theme.chatFeedbackHoverBg || 'rgba(107, 114, 128, 0.1)';
  const feedbackActiveBg = theme.chatFeedbackActiveBg || 'rgba(64, 128, 242, 0.1)';
  const feedbackBorderColor = theme.chatFeedbackBorderColor || 'transparent';
  const feedbackHoverBorderColor = theme.chatFeedbackHoverBorderColor || '#e5e7eb';
  const feedbackActiveBorderColor = theme.chatFeedbackActiveBorderColor || '#4080F2';

  // Citation links now use actual URLs with target="_blank" to open in new tabs
  // No need to intercept clicks anymore

  // Loading state - ONLY show loading spinner when isLoading (not streaming yet)
  if (message.isLoading && !message.isStreaming) {
    const isProactive = theme?.interfaceVariant === 'proactive';
    const loadingIcon = theme.standardLoadingIcon || 'spinner';
    const loadingText = theme.standardLoadingText || 'Generating response...';
    const textColor = isProactive ? '#1f2937' : (theme.standardBotBubbleText || '#111827');

    return (
      <div className="std-message-display" style={{
        background: isProactive ? '#ffffff' : (theme.standardBotBubbleBg || '#ffffff'),
        border: '1px solid #E5E7EB',
        borderRadius: '16px',
        padding: '14px 16px',
        marginBottom: '16px',
        boxShadow: '0 2px 6px rgba(0,0,0,0.04)'
      }}>
        {/* Loading animation - no icon */}
        <div style={{display: 'flex', alignItems: 'center', gap: 10, color: textColor, opacity: 0.7, fontSize: 14}}>
          {loadingIcon === 'spinner' ? (
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" className="animate-spin">
              <circle cx="12" cy="12" r="3" stroke="currentColor" strokeWidth="2"/>
              <path d="m12 1 0 6m0 10 0 6m11-7-6 0m-10 0-6 0m8.5-8.5 4.24-4.24m-12.02 12.02 4.24-4.24m8.48 0 4.24 4.24m-12.02-12.02 4.24 4.24" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
            </svg>
          ) : loadingIcon === 'dots' ? (
            <div className="loading-dots">
              <span>•</span><span>•</span><span>•</span>
            </div>
          ) : null}
          <span>{loadingText}</span>
        </div>
      </div>
    );
  }

  // Process citations and render content with inline links (Grok-style)
  const renderContentWithInlineCitations = () => {
    const base = renderMarkdownContent(message.content || '');
    let rawCitations: any = message?.metadata?.citations || message?.citation_sources || message?.metadata?.citation_sources || [];

    if (rawCitations && !Array.isArray(rawCitations) && typeof rawCitations === 'object') {
      rawCitations = Object.values(rawCitations);
    }

    const citations = (rawCitations || []).filter((c: any) => c && typeof c === 'object');

    const escapeHtml = (str: string) => (str || '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#039;');

    const getLabel = (c: any): string => {
      const raw = c?.title || c?.source_title || c?.page_title || c?.name || '';
      const cleaned = (raw || '').toString().trim();
      if (cleaned.length > 0) return cleaned;
      try {
        const url = new URL(c?.url || '');
        return url.hostname.replace('www.', '');
      } catch {
        return 'Source';
      }
    };

    // Replace [n] with simple inline citation links (Grok-style - no background, just text)
    const replacer = (match: string, numStr: string) => {
      const n = parseInt(numStr, 10) - 1;
      const c = citations[n];
      if (!c) return ''; // Invalid citation reference

      const fullLabel = getLabel(c);
      const label = escapeHtml(fullLabel);

      // If no URL (document/PDF), show as non-clickable styled span with document icon
      if (!c.url) {
        return `<span
          class="std-inline-citation std-inline-citation-doc"
          style="
            color: ${citationColor};
            background: ${citationBg};
            border: 1px solid ${citationBorder};
            padding: 2px 8px 2px 6px;
            border-radius: 6px;
            font-size: 12px;
            font-weight: 500;
            display: inline-flex;
            align-items: center;
            gap: 4px;
            margin: 0 2px;
            white-space: nowrap;
            vertical-align: middle;
            cursor: default;
          "
          title="${label}"
        ><svg width="10" height="10" viewBox="0 0 24 24" fill="none" style="flex-shrink: 0;">
          <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" stroke="${citationColor}" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
          <polyline points="14 2 14 8 20 8" stroke="${citationColor}" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>${label}</span>`;
      }

      // Inline citation link styled with theme colors
      // Use actual URL with target="_blank" to open in new tab
      return `<a
        class="std-inline-citation"
        href="${c.url}"
        target="_blank"
        rel="noopener noreferrer"
        style="
          color: ${citationColor};
          background: ${citationBg};
          border: 1px solid ${citationBorder};
          padding: 2px 8px 2px 6px;
          border-radius: 6px;
          text-decoration: none;
          font-size: 12px;
          font-weight: 500;
          display: inline-flex;
          align-items: center;
          gap: 4px;
          margin: 0 2px;
          white-space: nowrap;
          vertical-align: middle;
          transition: background 0.2s ease;
        "
        onmouseover="this.style.background='${citationHoverBg}';"
        onmouseout="this.style.background='${citationBg}';"
      ><svg width="10" height="10" viewBox="0 0 24 24" fill="none" style="flex-shrink: 0;">
        <path d="M10 14L21 3M21 3h-7m7 0v7" stroke="${citationColor}" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"/>
      </svg>${label}</a>`;
    };

    // Replace span citation markers first
    let html = base.replace(/<span[^>]*class="citation-marker"[^>]*data-source-number="(\d+)"[^>]*>.*?<\/span>/g, replacer);
    // Handle [Source N] format (new)
    html = html.replace(/\[Source\s*(\d+)\]/gi, replacer);
    // Also handle raw [n] patterns if present (legacy)
    html = html.replace(/\[(\d+)\]/g, replacer);

    return html;
  };

  const handleFeedbackClick = (type: 'thumbs_up' | 'thumbs_down' | 'need_review') => {
    if (feedbackState) return; // Already submitted

    if (type === 'need_review') {
      // Show reason options instead of submitting immediately
      setShowReasonOptions(true);
    } else {
      setFeedbackState(type);
      if (onFeedback && message.id) {
        onFeedback(message.id, type);
      }
    }
  };

  const handleReasonSelect = (reason: string) => {
    setFeedbackState('need_review');
    setShowReasonOptions(false);
    if (onFeedback && message.id) {
      onFeedback(message.id, 'need_review', reason);
    }
  };

  // Helper to detect system messages (welcome, clear history, etc.)
  const isSystemMessage = (msg: any) => {
    if (!msg.id || !msg.content) return false;

    // Check if it's a system message by ID pattern
    if (msg.id.startsWith('system-')) return true;

    // Check by content
    const content = msg.content.toLowerCase();
    if (content.includes('welcome! how can i help') ||
        content.includes('chat history has been cleared') ||
        content === 'welcome! how can i help you today?') {
      return true;
    }

    return false;
  };

  const isProactiveVariant = theme?.interfaceVariant === 'proactive';
  const textColor = isProactiveVariant ? '#1f2937' : (theme.standardBotBubbleText || '#111827');

  return (
    <div className="std-message-display" style={{
      background: isProactiveVariant ? '#ffffff' : (theme.standardBotBubbleBg || '#ffffff'),
      border: '1px solid #E5E7EB',
      borderRadius: '16px',
      padding: '14px 16px',
      boxShadow: '0 2px 6px rgba(0,0,0,0.04)',
      marginBottom: 16,
      maxWidth: '100%',
      overflow: 'hidden',
      boxSizing: 'border-box'
    }}>
      {/* Message content with inline citations - no icon */}
      <div
        ref={contentRef}
        className="std-message-content markdown-content"
        style={{
          fontSize: (theme.chatFontSizeResponse || 14) + 'px',
          color: textColor,
          lineHeight: 1.6,
          wordBreak: 'break-word',
          overflowWrap: 'break-word',
          maxWidth: '100%',
          overflow: 'hidden'
        }}
        dangerouslySetInnerHTML={{ __html: renderContentWithInlineCitations() }}
      />

      {/* Modern Feedback UI - 2025 Style */}
      {!message.isLoading && !message.isStreaming && message.id && !isSystemMessage(message) && (
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: 6,
          marginTop: 12,
          paddingTop: 8
        }}>
          {/* Good Response */}
          <button
            onClick={() => handleFeedbackClick('thumbs_up')}
            disabled={!!feedbackState}
            className="std-feedback-btn-modern"
            style={{
              background: feedbackState === 'thumbs_up' ? feedbackActiveBg : feedbackBg,
              color: feedbackState === 'thumbs_up' ? feedbackActiveColor : feedbackIconColor,
              border: `1px solid ${feedbackState === 'thumbs_up' ? feedbackActiveBorderColor : feedbackBorderColor}`,
              borderRadius: 8,
              padding: '6px 10px',
              cursor: feedbackState ? 'not-allowed' : 'pointer',
              opacity: feedbackState && feedbackState !== 'thumbs_up' ? 0.3 : 1,
              transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
              fontSize: 12,
              fontWeight: 500,
              display: 'flex',
              alignItems: 'center',
              gap: 4
            }}
            title="Good response"
            onMouseEnter={(e) => {
              if (!feedbackState) {
                e.currentTarget.style.color = feedbackIconHoverColor;
                e.currentTarget.style.background = feedbackHoverBg;
                e.currentTarget.style.borderColor = feedbackHoverBorderColor;
              }
            }}
            onMouseLeave={(e) => {
              if (!feedbackState) {
                e.currentTarget.style.color = feedbackIconColor;
                e.currentTarget.style.background = feedbackBg;
                e.currentTarget.style.borderColor = feedbackBorderColor;
              }
            }}
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M14 9V5a3 3 0 0 0-3-3l-4 9v11h11.28a2 2 0 0 0 2-1.7l1.38-9a2 2 0 0 0-2-2.3zM7 22H4a2 2 0 0 1-2-2v-7a2 2 0 0 1 2-2h3"/>
            </svg>
            {feedbackState === 'thumbs_up' && <span style={{fontSize: 11}}>Thanks!</span>}
          </button>

          {/* Bad Response */}
          <button
            onClick={() => handleFeedbackClick('thumbs_down')}
            disabled={!!feedbackState}
            className="std-feedback-btn-modern"
            style={{
              background: feedbackState === 'thumbs_down' ? feedbackActiveBg : feedbackBg,
              color: feedbackState === 'thumbs_down' ? feedbackActiveColor : feedbackIconColor,
              border: `1px solid ${feedbackState === 'thumbs_down' ? feedbackActiveBorderColor : feedbackBorderColor}`,
              borderRadius: 8,
              padding: '6px 10px',
              cursor: feedbackState ? 'not-allowed' : 'pointer',
              opacity: feedbackState && feedbackState !== 'thumbs_down' ? 0.3 : 1,
              transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
              fontSize: 12,
              fontWeight: 500,
              display: 'flex',
              alignItems: 'center',
              gap: 4
            }}
            title="Not helpful"
            onMouseEnter={(e) => {
              if (!feedbackState) {
                e.currentTarget.style.color = feedbackIconHoverColor;
                e.currentTarget.style.background = feedbackHoverBg;
                e.currentTarget.style.borderColor = feedbackHoverBorderColor;
              }
            }}
            onMouseLeave={(e) => {
              if (!feedbackState) {
                e.currentTarget.style.color = feedbackIconColor;
                e.currentTarget.style.background = feedbackBg;
                e.currentTarget.style.borderColor = feedbackBorderColor;
              }
            }}
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M10 15v4a3 3 0 0 0 3 3l4-9V2H5.72a2 2 0 0 0-2 1.7l-1.38 9a2 2 0 0 0 2 2.3zm7-13h2.67A2.31 2.31 0 0 1 22 4v7a2.31 2.31 0 0 1-2.33 2H17"/>
            </svg>
            {feedbackState === 'thumbs_down' && <span style={{fontSize: 11}}>Sorry!</span>}
          </button>

          {/* Needs Review */}
          <button
            onClick={() => handleFeedbackClick('need_review')}
            disabled={!!feedbackState}
            className="std-feedback-btn-modern"
            style={{
              background: feedbackState === 'need_review' ? feedbackActiveBg : feedbackBg,
              color: feedbackState === 'need_review' ? feedbackActiveColor : feedbackIconColor,
              border: `1px solid ${feedbackState === 'need_review' ? feedbackActiveBorderColor : feedbackBorderColor}`,
              borderRadius: 8,
              padding: '6px 10px',
              cursor: feedbackState ? 'not-allowed' : 'pointer',
              opacity: feedbackState && feedbackState !== 'need_review' ? 0.3 : 1,
              transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
              fontSize: 12,
              fontWeight: 500,
              display: 'flex',
              alignItems: 'center',
              gap: 4
            }}
            title="Needs review"
            onMouseEnter={(e) => {
              if (!feedbackState) {
                e.currentTarget.style.color = feedbackIconHoverColor;
                e.currentTarget.style.background = feedbackHoverBg;
                e.currentTarget.style.borderColor = feedbackHoverBorderColor;
              }
            }}
            onMouseLeave={(e) => {
              if (!feedbackState) {
                e.currentTarget.style.color = feedbackIconColor;
                e.currentTarget.style.background = feedbackBg;
                e.currentTarget.style.borderColor = feedbackBorderColor;
              }
            }}
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="12" r="10"/>
              <line x1="12" y1="8" x2="12" y2="12"/>
              <line x1="12" y1="16" x2="12.01" y2="16"/>
            </svg>
            {feedbackState === 'need_review' && <span style={{fontSize: 11}}>Noted!</span>}
          </button>

          {/* Save for Training - Only show for authenticated users in app (not widget/public) */}
          {message.id && !isWidgetMode && isAuthenticated && (
            <SaveAnswerButton
              messageId={message.id}
              conversationId={conversationId}
              clientId={clientId}
              disabled={!!feedbackState}
            />
          )}
        </div>
      )}

      {/* Reason Options */}
      {showReasonOptions && !feedbackState && (
        <div style={{
          marginTop: 12,
          padding: 12,
          background: '#F9FAFB',
          borderRadius: 8,
          border: `1px solid ${feedbackBorderColor}`
        }}>
          <div style={{
            fontSize: 12,
            fontWeight: 600,
            color: textColor,
            marginBottom: 10,
            opacity: 0.8
          }}>
            What went wrong?
          </div>
          <div style={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: 6
          }}>
            {REASON_OPTIONS.map((option) => (
              <button
                key={option.value}
                onClick={() => handleReasonSelect(option.value)}
                style={{
                  background: feedbackBg,
                  color: feedbackIconColor,
                  border: `1px solid ${feedbackBorderColor}`,
                  borderRadius: 16,
                  padding: '6px 12px',
                  fontSize: 11,
                  fontWeight: 500,
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                  whiteSpace: 'nowrap'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = feedbackHoverBg;
                  e.currentTarget.style.borderColor = feedbackHoverBorderColor;
                  e.currentTarget.style.color = feedbackIconHoverColor;
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = feedbackBg;
                  e.currentTarget.style.borderColor = feedbackBorderColor;
                  e.currentTarget.style.color = feedbackIconColor;
                }}
              >
                {option.label}
              </button>
            ))}
          </div>
        </div>
      )}

      <style>{`
        .std-message-display * {
          box-sizing: border-box;
        }

        .std-message-content {
          overflow-wrap: break-word;
          word-wrap: break-word;
          word-break: break-word;
        }

        .std-message-content p,
        .std-message-content div,
        .std-message-content span {
          max-width: 100%;
        }

        /* Override all global link styles for citations with theme colors */
        html body .chat-overlay .std-inline-citation,
        html body #ai-widget-container .std-inline-citation,
        html body #ai-chat-widget-container .std-inline-citation,
        .std-message-display .std-inline-citation,
        .std-message-content .std-inline-citation,
        .std-inline-citation {
          color: ${citationColor} !important;
          background: ${citationBg} !important;
          border: 1px solid ${citationBorder} !important;
          padding: 2px 8px 2px 6px !important;
          border-radius: 6px !important;
          text-decoration: none !important;
          font-size: 12px !important;
          font-weight: 500 !important;
          display: inline-flex !important;
          align-items: center !important;
          gap: 4px !important;
          margin: 0 2px !important;
          white-space: nowrap !important;
          vertical-align: middle !important;
          transition: background 0.2s ease !important;
          max-width: 100%;
        }

        html body .chat-overlay .std-inline-citation:hover,
        html body #ai-widget-container .std-inline-citation:hover,
        html body #ai-chat-widget-container .std-inline-citation:hover,
        .std-message-display .std-inline-citation:hover,
        .std-message-content .std-inline-citation:hover,
        .std-inline-citation:hover {
          background: ${citationHoverBg} !important;
          opacity: 1 !important;
        }

        html body .chat-overlay .std-inline-citation svg path,
        html body #ai-widget-container .std-inline-citation svg path,
        html body #ai-chat-widget-container .std-inline-citation svg path,
        .std-inline-citation svg path {
          stroke: ${citationColor} !important;
        }

        .std-feedback-btn:hover:not(:disabled) {
          background: #F9FAFB !important;
          transform: scale(1.05);
        }

        .std-feedback-btn:active:not(:disabled) {
          transform: scale(0.95);
        }

        .loading-dots {
          display: flex;
          gap: 2px;
        }

        .loading-dots span {
          animation: dot-pulse 1.4s infinite;
          font-size: 20px;
          line-height: 1;
        }

        .loading-dots span:nth-child(2) {
          animation-delay: 0.2s;
        }

        .loading-dots span:nth-child(3) {
          animation-delay: 0.4s;
        }

        @keyframes dot-pulse {
          0%, 80%, 100% { opacity: 0.3; }
          40% { opacity: 1; }
        }
      `}</style>
    </div>
  );
};

export default StandardMessageDisplay;
